export interface Problem {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  code: string;
  errors?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(problem.detail || problem.title || problem.code);
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
  }
}

export const NETWORK_ERROR = 'network.unreachable';

/**
 * Native apps keep the refresh token themselves (iOS Keychain) and exchange it in the request
 * body; the web never sees it, it rides in an HttpOnly cookie. See ADR-0004.
 */
export interface RefreshTokenStore {
  get(): Promise<string | null>;
  set(token: string | null): Promise<void>;
}

export interface TokenPair {
  access_token: string;
  refresh_token?: string | null;
}

export interface ClientOptions {
  baseUrl?: string;
  appVersion: string;
  /** Set by native clients; switches the session from the cookie to this store. */
  refreshStore?: RefreshTokenStore;
  /** Called when the refresh cookie is missing or revoked; the app should show sign-in / pairing. */
  onUnauthenticated?: () => void;
  onUpgradeRequired?: () => void;
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  random?: () => number;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  /** Pass a stable key so offline replays of the same mutation are deduplicated server-side. */
  idempotencyKey?: string;
  auth?: boolean;
  retries?: number;
  signal?: AbortSignal;
}

/** RFC 4122 v4 UUID via getRandomValues (crypto.randomUUID needs Safari 15.4). */
export function uuid4(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h: string[] = [];
  for (let i = 0; i < 16; i++) h.push((b[i]! + 0x100).toString(16).slice(1));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10).join('')}`;
}

export class HttpClient {
  private accessToken: string | null = null;
  private refreshing: Promise<boolean> | null = null;
  private readonly o: Required<Omit<ClientOptions, 'onUnauthenticated' | 'onUpgradeRequired' | 'refreshStore'>> &
    Pick<ClientOptions, 'onUnauthenticated' | 'onUpgradeRequired' | 'refreshStore'>;

  constructor(opts: ClientOptions) {
    this.o = {
      baseUrl: '',
      fetchImpl: (input, init) => fetch(input, init),
      sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
      random: Math.random,
      ...opts,
    };
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  hasAccessToken(): boolean {
    return this.accessToken !== null;
  }

  /** Stores tokens from a sign-in: the access token in memory, a native refresh token in the store. */
  async acceptTokens(t: TokenPair): Promise<void> {
    this.accessToken = t.access_token;
    if (this.o.refreshStore && t.refresh_token) await this.o.refreshStore.set(t.refresh_token);
  }

  /** The refresh token a native client holds, to revoke it on sign-out; null on the web. */
  async storedRefreshToken(): Promise<string | null> {
    return this.o.refreshStore ? this.o.refreshStore.get() : null;
  }

  /** Clears the native refresh token (sign-out). */
  async forgetSession(): Promise<void> {
    this.accessToken = null;
    if (this.o.refreshStore) await this.o.refreshStore.set(null);
  }

  /** Exchanges the refresh cookie (web) or stored refresh token (native) for an access token. Single-flight. */
  refresh(): Promise<boolean> {
    if (!this.refreshing) {
      this.refreshing = this.exchangeRefresh()
        .then(async (r) => {
          if (!r) {
            this.accessToken = null;
            return false;
          }
          await this.acceptTokens(r);
          return true;
        })
        .catch(async (e: unknown) => {
          if (e instanceof ApiError && e.status === 401) {
            await this.forgetSession();
            return false;
          }
          throw e;
        })
        .then(
          (ok) => {
            this.refreshing = null;
            return ok;
          },
          (e) => {
            this.refreshing = null;
            throw e;
          },
        );
    }
    return this.refreshing;
  }

  private async exchangeRefresh(): Promise<TokenPair | null> {
    const store = this.o.refreshStore;
    if (!store) return this.request<TokenPair>('/v1/auth/refresh', { method: 'POST', auth: false, retries: 1 });
    const token = await store.get();
    if (!token) return null;
    return this.request<TokenPair>('/v1/auth/refresh', { method: 'POST', auth: false, retries: 1, body: { refresh_token: token } });
  }

  async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const method = opts.method || 'GET';
    const auth = opts.auth !== false;
    const isMutation = method !== 'GET';
    const idem = isMutation ? opts.idempotencyKey || uuid4() : undefined;
    // Mutations only retry when an idempotency key makes the retry safe.
    const maxRetries = opts.retries !== undefined ? opts.retries : isMutation ? 2 : 3;

    let refreshed = false;
    if (auth && !this.accessToken) {
      await this.refresh();
      refreshed = true;
    }

    let attempt = 0;
    for (;;) {
      let res: Response;
      try {
        res = await this.o.fetchImpl(this.url(path, opts.query), {
          method,
          // Native sessions live in the store; never let a cookie jar carry one.
          credentials: this.o.refreshStore ? 'omit' : 'include',
          signal: opts.signal,
          headers: this.headers(auth, idem, opts.body !== undefined),
          body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        });
      } catch (e) {
        if ((e as Error).name === 'AbortError') throw e;
        if (attempt < maxRetries) {
          await this.backoff(attempt++);
          continue;
        }
        throw new ApiError({ status: 0, code: NETWORK_ERROR, title: 'Network unreachable' });
      }

      if (res.status === 401 && auth && !refreshed) {
        refreshed = true;
        if (await this.refresh()) continue;
      }
      if (res.status >= 500 && attempt < maxRetries) {
        await this.backoff(attempt++);
        continue;
      }
      if (res.status === 204) return undefined as T;
      const data = await this.parse(res);
      if (!res.ok) {
        const problem = toProblem(res.status, data);
        if (problem.code === 'client.upgrade_required') this.o.onUpgradeRequired?.();
        if (res.status === 401 && auth) this.o.onUnauthenticated?.();
        throw new ApiError(problem);
      }
      return data as T;
    }
  }

  private headers(auth: boolean, idem: string | undefined, hasBody: boolean): Record<string, string> {
    const h: Record<string, string> = { Accept: 'application/json', 'X-App-Version': this.o.appVersion };
    if (this.o.refreshStore) h['X-OBV-Client'] = 'native';
    if (hasBody) h['Content-Type'] = 'application/json';
    if (auth && this.accessToken) h.Authorization = `Bearer ${this.accessToken}`;
    if (idem) h['Idempotency-Key'] = idem;
    return h;
  }

  private url(path: string, query?: RequestOptions['query']): string {
    const parts: string[] = [];
    if (query) {
      for (const k of Object.keys(query)) {
        const v = query[k];
        if (v !== undefined && v !== null && v !== '') parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
      }
    }
    return this.o.baseUrl + path + (parts.length ? `?${parts.join('&')}` : '');
  }

  private async parse(res: Response): Promise<unknown> {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  /** Exponential backoff with full jitter: 0..min(8s, 400ms·2^n). */
  private backoff(attempt: number): Promise<void> {
    const cap = Math.min(8000, 400 * Math.pow(2, attempt));
    return this.o.sleep(Math.floor(this.o.random() * cap));
  }
}

function toProblem(status: number, data: unknown): Problem {
  if (data && typeof data === 'object' && 'code' in data) return { status, ...(data as Omit<Problem, 'status'>) };
  return { status, code: `http.${status}`, title: typeof data === 'string' ? data.slice(0, 200) : `HTTP ${status}` };
}
